import {ItemActionMenu, type ItemActionMenuToggleProps, type ItemMenuAction} from '@github-ui/list-item-action-menu'
import {testIdProps} from '@github-ui/test-id-props'
import type {Icon} from '@primer/octicons-react'
import {clsx} from 'clsx'
import {type ReactNode, type RefObject, useEffect, useRef} from 'react'

import {useNestedListViewItems} from '../context/ItemsContext'
import styles from './ActionBar.module.css'
import {useSuppressActions} from './hooks/use-suppress-actions'

export type NestedListItemActionBarProps = {
  /**
   * Description of the controls, e.g. "Issue actions". Used for the menu toggle's accessible name.
   */
  label?: string
  /**
   * Actions shown in the item's menu.
   */
  staticMenuActions?: ItemMenuAction[]
  /**
   * Override the icon used in the button that opens the action menu. Defaults to KebabHorizontalIcon.
   */
  anchorIcon?: Icon
  /**
   * Element the menu should be anchored to, when it shouldn't be the toggle itself.
   */
  anchorRef?: RefObject<HTMLElement | null>
  className?: string
  /**
   * Props forwarded to the menu toggle IconButton (e.g. `size`). `icon` is optional here
   * and defaults to `anchorIcon` or the kebab, so callers can tweak the toggle without repeating it.
   */
  overflowMenuToggleProps?: ItemActionMenuToggleProps
  /**
   * Controls rendered inline, before the menu.
   */
  children?: ReactNode
}

export function NestedListItemActionBar({
  anchorIcon,
  label = 'list item action bar',
  className,
  overflowMenuToggleProps,
  staticMenuActions,
  anchorRef,
  children,
}: NestedListItemActionBarProps) {
  const actionBarContainerRef = useRef<HTMLDivElement>(null)
  const {setAnyItemsWithActionBar, setHasResizableActionsWithActionBar} = useNestedListViewItems()
  const hasInlineActions = Boolean(children)

  useEffect(() => setAnyItemsWithActionBar(true), [setAnyItemsWithActionBar])
  useEffect(() => {
    if (hasInlineActions) setHasResizableActionsWithActionBar(true)
  }, [hasInlineActions, setHasResizableActionsWithActionBar])

  useSuppressActions(actionBarContainerRef)

  return (
    <div
      ref={actionBarContainerRef}
      aria-hidden="true"
      className={clsx(styles.container, !hasInlineActions && styles.withoutActions, className)}
      {...testIdProps('nested-list-view-item-action-bar-container')}
    >
      {children}
      <ItemActionMenu
        label={label}
        actions={staticMenuActions ?? []}
        anchorRef={anchorRef}
        toggleProps={{
          ...overflowMenuToggleProps,
          icon: overflowMenuToggleProps?.icon ?? anchorIcon,
        }}
      />
    </div>
  )
}
